import React, { useMemo } from 'react';
import { useRegion } from '../../contexts/RegionContext';
import type { BusinessClaimsVendorRowState } from '../../types/business-claims.types';

interface BusinessClaimsVendorEditorProps {
  rows: BusinessClaimsVendorRowState[];
  onRowsChange: (rows: BusinessClaimsVendorRowState[]) => void;
  overwriteExisting: boolean;
  onOverwriteExistingChange: (value: boolean) => void;
  loading?: boolean;
  error?: string | null;
  dateRangeLabel?: string;
}

function coverageLabel(coverage: BusinessClaimsVendorRowState['group']['coverage']): string {
  switch (coverage) {
    case 'complete':
      return 'Set';
    case 'partial':
      return 'Partial';
    default:
      return 'Unset';
  }
}

export function BusinessClaimsVendorEditor({
  rows,
  onRowsChange,
  overwriteExisting,
  onOverwriteExistingChange,
  loading,
  error,
  dateRangeLabel,
}: BusinessClaimsVendorEditorProps): React.ReactElement {
  const { formatCurrency } = useRegion();

  const [onlyNeedsReview, setOnlyNeedsReview] = React.useState(true);

  const visibleRows = useMemo(
    () =>
      onlyNeedsReview
        ? rows.filter((r) => r.group.coverage !== 'complete')
        : rows,
    [rows, onlyNeedsReview]
  );

  const visibleIds = useMemo(() => new Set(visibleRows.map((r) => r.group.id)), [visibleRows]);

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((r) => r.selected);
  const someVisibleSelected = visibleRows.some((r) => r.selected);

  const setRow = (id: string, patch: Partial<BusinessClaimsVendorRowState>) => {
    onRowsChange(rows.map((r) => (r.group.id === id ? { ...r, ...patch } : r)));
  };

  const toggleAllVisible = (selectAll: boolean) => {
    onRowsChange(
      rows.map((r) =>
        visibleIds.has(r.group.id) ? { ...r, selected: selectAll } : r
      )
    );
  };

  if (loading) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400" role="status">
        Loading business vendors for {dateRangeLabel ?? 'selected period'}…
      </p>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400">
        No business expense transactions in this date range. Adjust the date range in step 1 or
        categorise debits under Business Expenses.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Business-use by vendor
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {rows.length} vendor{rows.length === 1 ? '' : 's'} in{' '}
          {dateRangeLabel ?? 'this period'}. Set business-use % per vendor; values are saved to
          each transaction when you continue.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
        <label className="inline-flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={overwriteExisting}
            onChange={(e) => onOverwriteExistingChange(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Overwrite existing business-use %
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              When off, only transactions without a business % are updated.
            </span>
          </span>
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 shrink-0">
          <input
            type="checkbox"
            checked={onlyNeedsReview}
            onChange={(e) => setOnlyNeedsReview(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Show only needs review
        </label>
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                  }}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  disabled={visibleRows.length === 0}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  aria-label="Select all vendors"
                />
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Vendor
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Total
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                On file
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">
                Business %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {visibleRows.map((row) => (
              <tr key={row.group.id}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={row.selected}
                    onChange={() => setRow(row.group.id, { selected: !row.selected })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {row.group.vendorLabel}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row.group.transactionCount} transaction
                    {row.group.transactionCount === 1 ? '' : 's'}
                  </div>
                </td>
                <td className="px-3 py-3 text-right text-gray-900 dark:text-gray-100">
                  {formatCurrency(row.group.totalAmountAud)}
                </td>
                <td className="px-3 py-3 text-gray-600 dark:text-gray-400">
                  {row.group.businessUsePercent != null
                    ? `${row.group.businessUsePercent}%`
                    : '—'}
                  <span className="ml-2 inline-flex rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-xs">
                    {coverageLabel(row.group.coverage)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    disabled={!row.selected}
                    value={row.businessUsePercentInput}
                    onChange={(e) =>
                      setRow(row.group.id, { businessUsePercentInput: e.target.value })
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                    aria-label={`Business use percent for ${row.group.vendorLabel}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibleRows.length === 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          All vendors already have business-use % set. Turn off the filter to review or override
          them (enable overwrite above).
        </p>
      )}
    </div>
  );
}
